import React, { useState } from "react";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { Upgrade } from "@mui/icons-material";
import type { Participant } from "../../types";
import { GoogleHealthService } from "../../services/googleHealthService";
import { ApiError } from "../../utils/api";

const GOOGLE_AUTHORIZATION_HOST = "accounts.google.com";

type NotificationError = (
  title: string,
  message: string,
  action?: string,
) => void;

export type GoogleHealthScopeUpgradeButtonProps = {
  participant: Participant;
  studyCode?: string;
  enabled: boolean;
  disabled?: boolean;
  getAuthToken: () => Promise<string | null>;
  showError: NotificationError;
  /** Injectable only to keep the redirect behavior focused and testable. */
  navigateToAuthorizationUrl?: (authorizationUrl: string) => void;
};

/**
 * The backend deliberately exposes this narrow action only after it projects
 * an active Google connection. Older/default deployments omit that summary,
 * so this remains absent rather than guessing from legacy Fitbit fields.
 */
export const isActiveSyntheticGoogleHealthParticipant = (
  participant: Participant,
  studyCode?: string,
): boolean =>
  Boolean(
    studyCode &&
      participant.googleHealthSyntheticTest === true &&
      participant.studyCode === studyCode &&
      participant.healthConnection?.provider === "google_health" &&
      participant.healthConnection.status === "active",
  );

/** Only follow the expected Google OAuth host; never surface the URL itself. */
export const getTrustedGoogleAuthorizationUrl = (
  authorizationUrl: unknown,
): string | null => {
  if (typeof authorizationUrl !== "string") return null;

  try {
    const parsedUrl = new URL(authorizationUrl);
    if (
      parsedUrl.protocol !== "https:" ||
      parsedUrl.hostname !== GOOGLE_AUTHORIZATION_HOST
    ) {
      return null;
    }
    return parsedUrl.toString();
  } catch (_error) {
    return null;
  }
};

const isNotFoundError = (error: unknown): boolean =>
  error instanceof ApiError
    ? error.status === 404
    : Boolean(
        error &&
          typeof error === "object" &&
          "status" in error &&
          (error as { status?: unknown }).status === 404,
      );

/**
 * Researcher-only control for the one active synthetic Google connection.
 * A 404 means the API is using its default/read-probe authorization profile;
 * the control removes itself without retrying or falling back to another path.
 */
const GoogleHealthScopeUpgradeButton: React.FC<
  GoogleHealthScopeUpgradeButtonProps
> = ({
  participant,
  studyCode,
  enabled,
  disabled = false,
  getAuthToken,
  showError,
  navigateToAuthorizationUrl = (authorizationUrl) =>
    window.location.assign(authorizationUrl),
}) => {
  const [isUnavailable, setIsUnavailable] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  if (
    !enabled ||
    isUnavailable ||
    !isActiveSyntheticGoogleHealthParticipant(participant, studyCode)
  ) {
    return null;
  }

  const handleStartScopeUpgrade = async () => {
    if (disabled || isStarting || !studyCode) return;

    setIsStarting(true);
    try {
      const token = await getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Please sign in again before reauthorizing Google Health.",
          "Google Health",
        );
        return;
      }

      const response =
        await GoogleHealthService.startSyntheticScopeUpgradeAuthorization(
          participant.participantId,
          studyCode,
          token,
        );
      const authorizationUrl = getTrustedGoogleAuthorizationUrl(
        response?.authorizationUrl,
      );
      if (!authorizationUrl) {
        throw new Error("Unexpected Google authorization URL");
      }

      navigateToAuthorizationUrl(authorizationUrl);
    } catch (error) {
      if (isNotFoundError(error)) {
        // The route is intentionally absent under the default scope profile.
        // Do not expose a provider error or try a broader fallback flow.
        setIsUnavailable(true);
        return;
      }

      showError(
        "Google Health scope upgrade unavailable",
        "Could not start Google Health historical-scope reauthorization. Please try again.",
        "Google Health",
      );
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <Tooltip title="Reauthorize Google Health historical scope">
      <span>
        <IconButton
          size="small"
          color="secondary"
          aria-label="Reauthorize Google Health historical scope"
          onClick={handleStartScopeUpgrade}
          disabled={disabled || isStarting}
        >
          {isStarting ? <CircularProgress size={16} /> : <Upgrade />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default GoogleHealthScopeUpgradeButton;
